import React, { useState, useEffect } from "react";
import { FilterSelect, FilterSelectOption } from "./filter-select";
import { Input } from "../ui/input";
import { Plus, Minus } from "lucide-react";
import { FilterCondition, FilterField } from "@/interfaces/filter.interface";
import { cn } from "@/lib/utils";
import { FilterOperator } from "@/constants/filter.constant";
import { MultiSelectWithSearch } from "./filter-multi-select";

/**
 * Props for the AddFilterRow component
 */
interface AddFilterRowProps {
  /** Available filter fields */
  availableFields: FilterField[];
  /** Fields already used in other conditions */
  usedFields: string[];
  /** Callback fired when filter condition is added */
  onAdd: (condition: FilterCondition) => void;
  /** Callback fired when pending filter changes */
  onPendingFilterChange?: (pending: Partial<FilterCondition> | null) => void;
  /** Callback fired to remove last condition */
  onRemoveLast?: () => void;
  /** Callback fired to remove this row */
  onRemoveRow?: () => void;
  /** Whether a filter can be added */
  canAdd: boolean;
  /** Whether maximum filters reached */
  maxReached: boolean;
  /** Whether this is the last row. Defaults to false. */
  isLastRow?: boolean;
  /** Whether remove button is disabled. Defaults to false. */
  disableRemove?: boolean;
}

/**
 * AddFilterRow component displays a row for adding a new filter condition.
 * Allows selecting field, operator, and value before adding. Validates input
 * before enabling the add button. Supports different input types based on field.
 * 
 * @component
 * @param {AddFilterRowProps} props - Component props
 * @param {FilterField[]} props.availableFields - Available fields
 * @param {string[]} props.usedFields - Used field IDs
 * @param {(condition: FilterCondition) => void} props.onAdd - Add callback
 * @param {(pending: Partial<FilterCondition> | null) => void} [props.onPendingFilterChange] - Pending filter change callback
 * @param {() => void} [props.onRemoveLast] - Remove last callback
 * @param {() => void} [props.onRemoveRow] - Remove row callback
 * @param {boolean} props.canAdd - Whether can add
 * @param {boolean} props.maxReached - Whether max reached
 * @param {boolean} [props.isLastRow=false] - Whether last row
 * @param {boolean} [props.disableRemove=false] - Whether remove disabled
 * @returns {JSX.Element} Add filter row with field, operator, value inputs
 * 
 * @example
 * ```tsx
 * <AddFilterRow
 *   availableFields={fields}
 *   usedFields={used}
 *   onAdd={handleAdd}
 *   canAdd={true}
 *   maxReached={false}
 * />
 * ```
 */
export const AddFilterRow: React.FC<AddFilterRowProps> = ({
  availableFields,
  usedFields,
  onAdd,
  onPendingFilterChange,
  onRemoveLast,
  onRemoveRow,
  canAdd,
  maxReached,
  isLastRow = false,
  disableRemove = false,
}) => {
  const [field, setField] = useState<string>("");
  const [operator, setOperator] = useState<FilterOperator| "">("");
  const [value, setValue] = useState<string>("");
  const [values, setValues] = useState<string[]>([]);

  const fieldsOptions: FilterSelectOption[] = availableFields
    .filter((f) => !usedFields.includes(f.id))
    .map((field) => ({
      value: field.id,
      label: field.label,
    }));

  const selectedField = availableFields.find((f) => f.id === field);

  let operatorOptions: FilterSelectOption[] =
    selectedField?.operators
    ? Object.values(selectedField.operators).map(op => ({
          value: op,
        label: op.replace(/_/g, " ")
        }))
      : [];

  useEffect(() => {
    operatorOptions = selectedField?.operators
      ? Object.values(selectedField.operators).map((op) => ({
          value: op,
          label: op.replace(/_/g, " "),
        }))
      : [];
  }, [selectedField]);

  const renderValueInput = () => {
    if (!selectedField) {
      return (
        <Input
          placeholder="Value"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled
          className="w-full"
        />
      );
    }

    if (
      (selectedField.type === "select" ||
        selectedField.type === "multiselect") &&
      selectedField.options
    ) {
      const valueOptions: FilterSelectOption[] = selectedField.options.map(
        (opt) => ({
        value: opt,
        label: opt,
        })
      );
      if (selectedField.type === "multiselect") {
        return (
          <MultiSelectWithSearch
            options={valueOptions}
            value={values}
            onChange={(vals) => setValues(vals)}
            placeholder="Select values"
            className="w-full"
            />
        );
      }

      return (
        <FilterSelect
          options={valueOptions}
          value={value}
          onChange={(val) => setValue(val)}
          placeholder={selectedField.placeholder}
          className="w-full"
        />
      );
    }

    return (
      <Input
        type={selectedField.type === "number" ? "number" : selectedField.type === "date" ? "date" : "text"}
        placeholder={selectedField.placeholder || "Value"}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-full"
      />
    );
  };

  const computedValue = values.length > 0 ? values.join(",") : value;

  const isValid = !!selectedField && !!operator && (computedValue ?? "").trim() !== "";

  useEffect(() => {
    if (onPendingFilterChange) {
      if (isValid) {
        onPendingFilterChange({
          field,
          operator,
          value: computedValue,
        });
      } else {
        onPendingFilterChange(null);
      }
    }
  }, [field, operator, value, values, isValid, onPendingFilterChange]);

  const handleAdd = () => {
    if (!isValid) return;
    const newCondition: FilterCondition = {
      id: `filter-${Date.now()}-${Math.random()}`,
      field,
      operator,
      value: computedValue,
      values: values
    };
    onAdd(newCondition);

    // reset
    setField("");
    setOperator("");
    setValue("");
    setValues([]);
    if (onPendingFilterChange) onPendingFilterChange(null);
  };

  const handleRemoveRow = () => {
    if (disableRemove) return;

    if (onRemoveRow) {
      onRemoveRow();
    }

    setField("");
    setOperator("");
    setValue("");
    setValues([]);
    if (onPendingFilterChange) onPendingFilterChange(null);
  };

  return (
    <div className="flex items-center gap-2">
      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
        <button
          type="button"
          onClick={handleRemoveRow}
          disabled={disableRemove}
          className={cn(
            "h-6 w-6 rounded-full p-0 flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:bg-gray-200 disabled:text-gray-400",
            disableRemove ? "bg-gray-200 text-gray-400" : "bg-primary-500 hover:bg-primary-600 text-white"
          )}
          aria-label="Remove add filter row"
        >
          <Minus className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 min-w-0 max-w-64">
        <FilterSelect
          options={fieldsOptions}
          value={field}
          onChange={(newField) => {
            setField(newField);
            setValue("");
            setValues([]);
            setOperator("");
          }}
          placeholder="Select a field"
          className="w-full"
        />
      </div>

      <div className="flex-1 min-w-0 max-w-64">
        <FilterSelect
          options={operatorOptions}
          value={operator}
          onChange={(value) => setOperator(value as FilterOperator)}
          className="w-full"
          placeholder="Select an operator"
        />
      </div>

      <div className="flex-1 min-w-0">
        {renderValueInput()}
      </div>

      {!isLastRow && (
        <button
          type="button"
          onClick={handleAdd}
          disabled={!isValid}
          className={cn(
            "h-6 w-6 rounded-full p-0 flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
            !isValid
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-primary-500 hover:bg-primary-600 text-white"
          )}
          aria-label="Add filter"
        >
          <Plus className="h-5 w-5" />
        </button>
      )}
      {isLastRow && <div className="w-6 h-6 flex-shrink-0" />}
    </div>
  );
};

